import { useState, useEffect } from 'react';
import clsx from 'clsx';
import useFetchDashboard from '../api/useFetchDashboard';
import useFetchOrganizationTutorials from '../../api/useFetchOrganizationTutorials';
import useFetchIam from '../../api/useFetchIam';
import TermsOfServiceDialog from '../../../../components/TermsOfServiceDialog';
import useTermsOfService from '../../../../hooks/useTermsOfService';
import OnboardingFlow from '../../dashboard/components/OnboardingFlow';
import Onboarding from '../components/Onboarding';
import TrialWalkthrough from '../components/TrialWalkthrough';
import DashboardKpiCards from '../components/DashboardKpiCards';
import DashboardSelectionReminders from '../components/DashboardSelectionReminders';
import DashboardTodaySchedule from '../components/DashboardTodaySchedule';
import DashboardActivityInfo from '../components/DashboardActivityInfo';
import DashboardProgressSection from '../components/DashboardProgressSection';
import { ALL_USERS_VALUE } from '../components/DashboardOrganizationUserSelect';

export default function DashboardTemplate() {
  const { shouldShowTermsDialog, closeTermsDialog } = useTermsOfService();
  const [isOnboardingProcessing, setIsOnboardingProcessing] = useState(false);
  // 担当者で全セクションを横断的に絞り込む（ALL_USERS_VALUE = 全担当者）
  const [organizationUserValue, setOrganizationUserValue] = useState<string>(ALL_USERS_VALUE);
  const organizationUserId =
    organizationUserValue === ALL_USERS_VALUE ? undefined : organizationUserValue;

  // IAM取得（プランタイプ判定用）
  const { data: iam, isLoading: isIamLoading } = useFetchIam();

  // データ取得
  const {
    data: tutorials,
    isLoading: isTutorialsLoading,
    isError: isTutorialsError,
    refetch: refetchTutorials,
  } = useFetchOrganizationTutorials();
  const { data: dashboard, isLoading: isDashboardLoading } = useFetchDashboard(organizationUserId);

  // チュートリアル完了時の処理中フラグリセット
  useEffect(() => {
    if (
      tutorials &&
      tutorials.records.length > 0 &&
      tutorials.records.every((tutorial) => tutorial.is_finished)
    ) {
      setIsOnboardingProcessing(false);
    }
  }, [tutorials]);

  // トライアルプラン判定
  const isTrial = iam?.plan_type === 'trial';

  // オンボーディング表示判定
  // エラー時は通常のダッシュボードを表示するため、オンボーディングを表示しない
  const shouldShowTrialWalkthrough =
    !isTutorialsLoading &&
    !isTutorialsError &&
    !isOnboardingProcessing &&
    isTrial &&
    (!tutorials || tutorials.records.length === 0);
  const shouldShowOnboardingFlow =
    !isTutorialsLoading &&
    !isTutorialsError &&
    !isOnboardingProcessing &&
    !isTrial &&
    (!tutorials || tutorials.records.length === 0);
  const shouldShowOnboardingSetup =
    !isTutorialsLoading &&
    !isTutorialsError &&
    !isOnboardingProcessing &&
    tutorials &&
    tutorials.records.length > 0 &&
    tutorials.records.some((tutorial) => !tutorial.is_finished);

  // データ読み込み中（IAMとチュートリアル両方の完了を待つ）
  if (isTutorialsLoading || isIamLoading) {
    return <div className="fixed inset-0 bg-white" style={{ zIndex: 9999 }}></div>;
  }

  // トライアルユーザー向けウォークスルー
  if (shouldShowTrialWalkthrough) {
    return (
      <TrialWalkthrough
        onComplete={() => {
          setIsOnboardingProcessing(true);
          void refetchTutorials().finally(() => {
            setIsOnboardingProcessing(false);
          });
        }}
      />
    );
  }

  // 初期オンボーディング（welcome/selectステップ）
  if (shouldShowOnboardingFlow) {
    return (
      <div className="fixed inset-0 bg-white" style={{ zIndex: 9999 }}>
        <OnboardingFlow onProcessingStart={() => setIsOnboardingProcessing(true)} />
      </div>
    );
  }

  // オンボーディング処理中
  if (isOnboardingProcessing) {
    return <div className="fixed inset-0 bg-white" style={{ zIndex: 9999 }}></div>;
  }

  return (
    <div className={clsx('hidden-scrollbar flex flex-col')}>
      {/* setupステップ（未完了チュートリアルがある場合） */}
      {shouldShowOnboardingSetup ? (
        <div className="mb-4">
          <Onboarding
            onComplete={() => setIsOnboardingProcessing(true)}
            tutorials={tutorials}
            refetchTutorials={refetchTutorials}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-sm p-lg">
          {/* KPI カード行（担当者絞り込み対象外） */}
          <DashboardKpiCards />

          {/* 進捗状況（フル幅、担当者セレクトと連動） */}
          <DashboardProgressSection organizationUserId={organizationUserId} />

          {/* メインコンテンツ（左: 未対応の選考 / 右: 本日の予定 + アップデート情報）
              担当者 Select の絞り込みは「本日の予定」のみに反映する */}
          <section className="grid grid-cols-1 gap-sm xl:h-[700px] xl:grid-cols-[594fr_536fr] xl:grid-rows-1">
            <DashboardSelectionReminders />
            <div className="flex flex-col gap-sm xl:min-h-0">
              <DashboardTodaySchedule
                jobChangeProcesses={dashboard?.job_change_processes ?? []}
                isLoading={isDashboardLoading}
                organizationUserValue={organizationUserValue}
                onOrganizationUserChange={setOrganizationUserValue}
              />
              <DashboardActivityInfo
                activities={dashboard?.activities ?? []}
                isLoading={isDashboardLoading}
              />
            </div>
          </section>
        </div>
      )}
      <TermsOfServiceDialog open={shouldShowTermsDialog} onClose={closeTermsDialog} />
    </div>
  );
}
